import {Cart} from "@connox/typescript-library";
import * as React from "react";
import {useTranslation} from "react-i18next";
import styled from "styled-components";
import {useCheckoutContext} from "../../checkout/CheckoutContextProvider/CheckoutContextProvider";
import {getShopId} from "../../lib/getShopId";
import {useCartContext} from "../CartContextProvider/CartContextProvider";
import {DeliveryTime} from "../DeliveryTime/DeliveryTime";

interface ILineProps {
  amount: string;
  mwst?: boolean;
  text: string;
}

const CartDetails: React.FC<{reduced?: boolean}> = (props) => {
  let cart: Cart;
  try {
    cart = new Cart(getShopId(), useCheckoutContext().checkout.cartData);
  } catch (err) {
    cart = useCartContext().cart;
  }
  const {t} = useTranslation("cart");

  const shippingPositions = cart.positions.filter((pos) => pos.type === "Shipping");
  const discountPositions = cart.positions.filter(
    (pos) => (!pos.reference || pos.reference.itemId === null) && pos.type !== "Shipping",
  );

  const hasRebate = cart.valueRebate && parseInt(cart.valueRebate.amount, 10) > 0;

  const DetailLine: React.FC<ILineProps> = (dlProps) => (
    <div>
      <span>{dlProps.text}</span>
      <span>{dlProps.amount === t("gratis") ? <strong>{dlProps.amount}</strong> : dlProps.amount}</span>
    </div>
  );

  const StrongLine: React.FC<ILineProps> = (slProps) => (
    <div>
      <span>
        <strong>{slProps.text}</strong>&nbsp;{slProps.mwst && t("inkl. MwSt")}
      </span>
      <span>
        <strong>{slProps.amount}</strong>
      </span>
    </div>
  );

  const Discounts: React.FC = () => {
    if (!discountPositions) {
      return null;
    }

    return (
      <React.Fragment>
        {discountPositions.map((pos, key) => {
          return <DetailLine key={key} text={pos.text} amount={pos.amount.formatted} />;
        })}
      </React.Fragment>
    );
  };

  const Taxes: React.FC = () => {
    return (
      <React.Fragment>
        <DetailLine amount={cart.amountNet.formatted} text={t("Nettobetrag")} />
        {cart.taxAmounts.map((tax, key) => {
          return (
            <DetailLine
              key={key}
              text={t("MwSt. ({{taxPercent}})", {taxPercent: tax.percent.formatted})}
              amount={tax.amount.formatted}
            />
          );
        })}
      </React.Fragment>
    );
  };

  const Shipping: React.FC = () => {
    if (!shippingPositions) {
      return null;
    }

    return (
      <React.Fragment>
        {shippingPositions.map((pos, key) => {
          if (pos.messages.some((msg) => msg.type === "error")) {
            return null;
          }
          const amount = pos.amount.amount === "0.00" ? t("gratis") : pos.amount.formatted;
          const {deliveryDaysMin, deliveryDaysMax} = cart.shippingInformation;

          return (
            <div key={key}>
              <span>
                {pos.text} (<DeliveryTime min={deliveryDaysMin} max={deliveryDaysMax} />)
              </span>
              <span>{amount}</span>
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  const DetailsListReduced: React.FC = () => (
    <React.Fragment>
      <Shipping />
      <div>
        <span>
          <strong>{t("Summe")}</strong>&nbsp;{t("inkl. MwSt")}
        </span>
        <span>
          <strong>{cart.valuePayable.formatted}</strong>
        </span>
      </div>
    </React.Fragment>
  );

  const DetailsList: React.FC = () => (
    <React.Fragment>
      <StrongLine text={t("Zwischensumme")} amount={cart.valueSubtotal.formatted} mwst />
      {cart.isCorporateCustomer && <Taxes />}
      <Discounts />
      <Shipping />
      <hr />
      <StrongLine text={t("Summe")} amount={cart.amountGross.formatted} mwst />
      {hasRebate && cart.valueRebate && (
        <React.Fragment>
          <DetailLine text={t("Gutschein")} amount={cart.valueRebate.formatted} />
          <StrongLine text={t("Zu zahlender Betrag")} amount={cart.valuePayable.formatted} mwst />
        </React.Fragment>
      )}
    </React.Fragment>
  );

  return (
    // class 'CartDetails' is still in use by ShoppingCart to
    // alternatively set bg color to white in desktop view
    <CartDetailsDiv className="CartDetails">{props.reduced ? <DetailsListReduced /> : <DetailsList />}</CartDetailsDiv>
  );
};

export default CartDetails;

const CartDetailsDiv = styled.div`
  margin-bottom: 1em;
  box-sizing: border-box;
  display: inline-block;
  > div {
    display: flex;
    > span,
    > strong {
      flex: 1 1 85%;
    }
    > span + span,
    > strong + strong {
      flex: 1 0 15%;
      text-align: right;
    }
  }
  padding: 1em;
  width: 100%;
  line-height: 1.6;
  /* CAUTION! bg color will be alternatively set in ShoppingCart */
  background-color: #fafafa;
`;
